<script setup lang="ts">
import store from "@/store";
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { toast } from "@/common/utils/toast";

import { formList, formAdd, formDel } from "@/common/api/rest/form/form";

import type { FormType, FormPayloadType } from "@/common/types/form";

import { RegisterTheme } from "@/common/themes";
import { formsForm } from "@/components";
import {
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const tableHeader = computed(() => [
  { key: "code", label: t("common.labels.code") },
  { key: "name", label: t("common.labels.name") },
  { key: "method", label: t("common.labels.method") },
]);

const tableData = ref<FormType[]>([]);

const payload = ref<FormPayloadType | null>(null);
const selected = ref<FormType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const isLoading = ref(false);
const showModalAdd = ref(false);
const showModalDel = ref(false);

const id = computed(() => route.params.id as string);

const delMessage = computed(() => {
  const message = t("form.texts.del_confirm", { concat: selected.value?.code });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

function fetchForms() {
  isLoading.value = true;
  formList(id.value, {
    search: search.value,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addItemConfirm() {
  if (!payload.value) return;

  showModalAdd.value = false;
  store.commit("SET_LOADING", true);

  formAdd(id.value, payload.value)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        router.push(`/${id.value}/register/forms/edit/${data.group_id}`);
      } else {
        toast({ message: data.message, type: "error" });
        showModalAdd.value = true;
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
      showModalAdd.value = true;
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editItem(data: FormType) {
  router.push(`/${id.value}/register/form/edit/${data.form_id}`);
}

function delItem(data: FormType) {
  showModalDel.value = true;
  selected.value = data;
}

function delItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function delItemConfirm() {
  if (!selected.value) return;

  showModalDel.value = false;
  store.commit("SET_LOADING", true);

  formDel(selected.value?.form_id as string)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        toast({ message: data.message, type: "success" });
        selected.value = null;
        fetchForms();
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function searchItem(value: string) {
  search.value = value;
  fetchForms();
}

function changePage(value: number) {
  page.value = value;
  fetchForms();
}

onMounted(() => {
  if (id.value) fetchForms();
});
</script>

<template>
  <register-theme tab-active="report">
    <template #title>
      <svg-icon icon="form" size="2rem" />
      {{ t("common.labels.reports") }}
    </template>

    <template #content>
      <baseTable
        :header="tableHeader"
        :data="tableData"
        :loading="isLoading"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :edit="true"
        :del="true"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="addItem()"
        @btn:edit="editItem"
        @btn:del="delItem"
      />
    </template>
  </register-theme>

  <base-modal
    :show="showModalAdd"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.confirm')"
    :button-close="t('common.labels.cancel')"
    @close="addItemCancel()"
    @confirm="addItemConfirm()"
  >
    <template #content>
      <forms-form
        :title="t('common.labels.add')"
        :data="payload"
        @update-data="payload = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showModalDel"
    :title="t('form.texts.del_form')"
    :message="delMessage"
    @cancel="delItemCancel()"
    @confirm="delItemConfirm()"
  />
</template>
